'use client';

// Componente REUTILIZABLE de subida de un PDF/soporte. Se usa en cualquier
// formulario que pida un documento: muestra un input de archivo + botón "Subir";
// al subir llama subirDocumento (POST /documentos multipart), muestra un spinner
// y al terminar guarda el `path` devuelto (onChange) mostrando el nombre del
// archivo y un enlace "ver". Los errores se muestran como toast (sonner).

import { useRef, useState } from 'react';
import { FileUp, Loader2, Paperclip, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  subirDocumento,
  type CarpetaDocumental,
  type DocumentoSubido,
} from '@/lib/documentos';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface CampoPDFProps {
  label: string;
  carpeta?: CarpetaDocumental;
  expedienteId?: string;
  nombre?: string;
  tipoDocumento?: string;
  /** path (storage_key) ya guardado, si lo hay. */
  value?: string;
  /** Se invoca con el `path` y los metadatos completos tras subir. */
  onChange: (path: string, meta: DocumentoSubido) => void;
  disabled?: boolean;
}

export default function CampoPDF({
  label,
  carpeta,
  expedienteId,
  nombre,
  tipoDocumento,
  value,
  onChange,
  disabled,
}: CampoPDFProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [subido, setSubido] = useState<DocumentoSubido | null>(null);

  async function handleSubir() {
    if (!file) {
      toast.error('Seleccione un archivo primero.');
      return;
    }
    setSubiendo(true);
    try {
      const meta = await subirDocumento({
        file,
        carpeta,
        expedienteId,
        nombre: nombre || file.name,
        tipoDocumento,
      });
      setSubido(meta);
      onChange(meta.path, meta);
      toast.success('Documento subido.');
    } catch (err) {
      toast.error('No se pudo subir el documento', {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSubiendo(false);
    }
  }

  function limpiar() {
    setFile(null);
    setSubido(null);
    onChange('', {} as DocumentoSubido);
    if (inputRef.current) inputRef.current.value = '';
  }

  const yaSubido = subido || value;

  return (
    <div className="space-y-2">
      <Label>{label}</Label>

      {yaSubido ? (
        // Estado: documento ya subido → muestra nombre + enlace "ver" + quitar.
        <div className="flex items-center gap-2 rounded-md border border-input bg-accent/40 px-3 py-2 text-sm">
          <Paperclip className="size-4 shrink-0 text-primary" />
          <span className="min-w-0 flex-1 truncate">
            {subido?.filename || nombre || file?.name || 'Documento'}
          </span>
          {subido?.url && (
            <a
              href={subido.url}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 font-medium text-primary underline underline-offset-2"
            >
              ver
            </a>
          )}
          {!disabled && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-7 shrink-0 p-0"
              onClick={limpiar}
              aria-label="Quitar documento"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      ) : (
        // Estado: sin subir → input de archivo + botón "Subir".
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/*,.pdf,.doc,.docx"
            disabled={disabled || subiendo}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="cursor-pointer"
          />
          <Button
            type="button"
            onClick={handleSubir}
            disabled={disabled || subiendo || !file}
            className="shrink-0"
          >
            {subiendo ? (
              <>
                <Loader2 className="size-4 animate-spin" /> Subiendo…
              </>
            ) : (
              <>
                <FileUp className="size-4" /> Subir
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
